<template>
  <BreadCrumb class="hidden sm:block" />
  <CollectionPage :entry="entry" :collection="collection" />
</template>

<script>
import gql from 'graphql-tag';
import { stripLocale } from '@/utils/i18n';
import { SeomaticFragment, EntryPageFragment, ShopHomeBreadcrumbFragment } from '@/mixins/entryPage';
import CollectionPage, { CollectionPageFragment } from '@/components/shop/collections/CollectionPage';
import BreadCrumb from '@/components/layout/BreadCrumb';

export const CollectionPageQuery = gql`
  query CollectionPageQuery($site: String!, $uri: String!) {
    ...ShopHomeBreadcrumbFragment
    entry(section: "collections", site: [$site], uri: [$uri]) {
      ...EntryPageFragment
      ...CollectionPageFragment
    }

    seomatic(site: $site, uri: $uri, asArray: true) {
      ...SeomaticFragment
    }
  }

  ${EntryPageFragment}
  ${SeomaticFragment}
  ${CollectionPageFragment}
  ${ShopHomeBreadcrumbFragment}
`;

export const PRODUCTS_PER_PAGE = 3;

export default defineNuxtComponent({
  name: 'CollectionPageController',

  components: {
    CollectionPage,
    BreadCrumb,
  },

  async asyncData() {
    const route = useRoute();
    const { $config, $shopifyCollectionRequest, $craftRequest } = useNuxtApp();
    const { locale } = useNuxtApp().$i18n;

    const uri = stripLocale(route.path, locale.value);
    const { handle } = route.params;
    const variables = { site: locale.value, uri };

    // FIXME: set default limit for all-products collection for a/b testing
    const limit = route.path.includes('all-products')
      ? 250
      : parseInt(route.query.limit) || $config.public.collectionProductsPerPage;

    const collectionRequest = $shopifyCollectionRequest({ handle, limit, lang: locale.value });
    const entryRequest = $craftRequest(CollectionPageQuery, variables);
    const [collection, { entry, seomatic, shopHomeEntry }] = await Promise.all([collectionRequest, entryRequest]);

    return { collection, entry, seomatic, shopHomeEntry };
  },

  data() {
    return {
      shopHomeEntry: null,
      collection: null,
      entryPage: null,
    };
  },

  created() {
    this.entryPage = useEntryPage(this.entry, this.seomatic, {
      content_category: 'shop',
      collection: this.entry?.collectionHandle,
    });

    if (process.server && !this.collection) return this.fetchProducts();

    this.buildBreadCrumb();
  },

  watch: {
    $route(to, from) {
      const newLimit = to.query.limit;
      const oldLimit = from.query.limit;
      if (newLimit !== oldLimit) this.fetchProducts(newLimit);
    },
  },

  mounted() {
    // FIXME: set default limit for all-products collection for a/b testing
    const limit = this.$route.path.includes('all-products')
      ? 250
      : parseInt(this.$route.query.limit) || this.$config.public.collectionProductsPerPage;

    if (!this.collection) this.fetchProducts(limit);
  },

  methods: {
    fetchProducts(optionalLimit = null) {
      const limit = optionalLimit ?? this.$config.public.collectionProductsPerPage;
      const { handle } = this.$route.params;

      return this.$shopifyCollectionRequest({ handle, limit, lang: this.$i18n.locale }).then((collection) => {
        this.collection = collection;
      });
    },

    /**
     * overwrite breacrumb builder to inject shop-root
     */
    buildBreadCrumb() {
      const breadCrumb = [];

      if (this.shopHomeEntry)
        breadCrumb.push({
          label: this.shopHomeEntry.title,
          route: this.$localeRootPath(this.shopHomeEntry.uri),
        });

      if (this.entry)
        breadCrumb.push({
          label: this.entry.title,
          route: this.$localeRootPath(this.entry.uri),
        });

      this.entryPage.setBreadCrumb(breadCrumb);
    },
  },
});
</script>
